import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TESTIDS } from "@/constants/testIds";
import {
  TrendingUp, AlertCircle, Package, Plus, CheckCircle2, Truck,
  ChevronRight, Activity, ClipboardList, BookOpen,
} from "lucide-react";
import { NewCompanyModal } from "@/pages/CompaniesPage";
import { NewOrderModal } from "@/pages/PipelinePage";
import { NewDocketModal } from "@/pages/DocketsPage";
import { NewInventoryModal } from "@/pages/InventoryPage";
import { NewContactModal } from "@/pages/ContactsPage";

const ORDER_STAGES = ["Orders In", "Picking", "To Invoice", "Completed"];
const WORKFLOW_STAGES = ["Orders In", "Picking", "To Invoice"];

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [dockets, setDockets] = useState([]);
  const [products, setProducts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [err, setErr] = useState("");
  const [quickAdd, setQuickAdd] = useState(null); // "order" | "docket" | "company" | "product" | null

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/dashboard/stats"),
      api.get("/orders"),
      api.get("/dockets"),
      api.get("/inventory"),
      api.get("/companies"),
    ])
      .then(([s, o, d, p, c]) => {
        if (!alive) return;
        setStats(s.data); setOrders(o.data); setDockets(d.data);
        setProducts(p.data); setCompanies(c.data);
      })
      .catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, []);

  // Computed values
  const pipelineValue = orders.reduce((sum, o) => {
    const p = products.find((x) => x.id === o.Product_ID);
    return sum + (o.Order_Status !== "Cancelled" && o.Order_Status !== "Completed"
      ? (p?.MSRP || 0) * (o.Quantity_Ordered || 0) : 0);
  }, 0);

  const today = new Date().toISOString().slice(0, 10);
  const overdueDockets = dockets.filter((d) => d.Docket_Status === "Pending" && d.Pickup_Deadline < today).length;
  const lowStockItems = products.filter((p) => (p.Total_Stock - p.Allocated_Stock) < p.Min_Stock_Warning);
  const upcomingDockets = dockets
    .filter((d) => d.Docket_Status === "Pending" || d.Docket_Status === "Scheduled")
    .sort((a, b) => (a.Pickup_Deadline || "").localeCompare(b.Pickup_Deadline || ""))
    .slice(0, 5);

  const workflowCounts = WORKFLOW_STAGES.reduce((acc, s) => {
    acc[s] = stats?.orders_by_status?.[s] || 0;
    return acc;
  }, {});

  // Invoiced (Completed) orders whose dockets are still not all Picked Up,
  // AND it's been ≥ 2 days since invoicing. These are the cards most likely
  // to be forgotten — admin probably wants to chase the customer.
  const stalledInvoiced = orders.filter((o) => {
    if (o.Order_Status !== "Completed") return false;
    const days = (() => {
      const iso = o.Invoiced_Date || o.Completed_At;
      if (!iso) return null;
      const ms = Date.now() - new Date(iso).getTime();
      return Number.isNaN(ms) ? null : Math.floor(ms / 86400000);
    })();
    if (days === null || days < 2) return false;
    const orderDockets = dockets.filter((d) => d.Order_ID === o.id);
    if (!orderDockets.length) return false;
    return orderDockets.some((d) => d.Docket_Status === "Pending" || d.Docket_Status === "Scheduled");
  });

  const allClear = overdueDockets === 0 && lowStockItems.length === 0 && stalledInvoiced.length === 0;
  const firstName = (user.full_name || user.email).split(" ")[0].split("@")[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Dashboard</h1>
          <p className="text-sm text-zinc-500 mt-1">Welcome back{firstName ? `, ${firstName}` : ""} — here's what's happening today.</p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/manual")}
          data-testid="dashboard-user-manual-link"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-600 hover:text-brand-700 border border-zinc-200 hover:border-brand-300 rounded-lg px-3 py-2 bg-white transition-colors"
          title="Open the Edenstone Ops user manual"
        >
          <BookOpen className="w-3.5 h-3.5" /> User Manual
        </button>
      </div>

      {err && <div className="es-card p-4 text-sm text-red-700">{err}</div>}

      {/* KPI tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <KpiTile
          testid={TESTIDS.dashKpiOrders}
          label="Pipeline Value"
          value={`$${Math.round(pipelineValue).toLocaleString()}`}
          icon={TrendingUp}
          accent="bg-green-100 text-green-700"
        />
        <KpiTile
          testid={TESTIDS.dashKpiDockets}
          label="Overdue Dockets"
          value={overdueDockets}
          icon={AlertCircle}
          accent="bg-red-100 text-red-600"
        />
        <KpiTile
          testid={TESTIDS.dashKpiLowstock}
          label="Low Stock Alerts (by product)"
          value={lowStockItems.length}
          icon={Package}
          accent="bg-amber-100 text-amber-700"
        />
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap gap-3">
        <QuickAction testid="quick-new-order" onClick={() => setQuickAdd("order")}>New Order</QuickAction>
        <QuickAction testid="quick-new-docket" onClick={() => setQuickAdd("docket")}>New Docket</QuickAction>
        <QuickAction testid="quick-new-company" onClick={() => setQuickAdd("company")}>New Company</QuickAction>
        <QuickAction testid="quick-new-contact" onClick={() => setQuickAdd("contact")}>New Contact</QuickAction>
        <QuickAction testid="quick-new-product" onClick={() => setQuickAdd("product")}>New Product</QuickAction>
      </div>

      {/* Alerts row */}
      {allClear ? (
        <div className="es-card p-12 flex flex-col items-center justify-center text-center" data-testid="dash-all-clear">
          <div className="w-14 h-14 rounded-full border-2 border-green-500 flex items-center justify-center mb-3">
            <CheckCircle2 className="w-7 h-7 text-green-600" />
          </div>
          <div className="font-semibold text-zinc-900">All clear!</div>
          <div className="text-sm text-zinc-500 mt-1">No items need immediate attention.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {overdueDockets > 0 && (
            <AlertCard
              testid="alert-overdue"
              icon={AlertCircle}
              tone="red"
              title={`${overdueDockets} overdue docket${overdueDockets === 1 ? "" : "s"}`}
              detail="Past their pickup deadline."
              cta="Open Dispatch"
              onClick={() => navigate("/dockets")}
            />
          )}
          {lowStockItems.length > 0 && (
            <AlertCard
              testid="alert-low-stock"
              icon={Package}
              tone="amber"
              title={`${lowStockItems.length} product${lowStockItems.length === 1 ? "" : "s"} below minimum`}
              detail={lowStockItems.slice(0, 3).map((p) => p.Product_Name).join(" · ") + (lowStockItems.length > 3 ? "…" : "")}
              cta="View low-stock"
              onClick={() => navigate("/inventory?filter=low")}
            />
          )}
          {stalledInvoiced.length > 0 && (
            <AlertCard
              testid="alert-stalled-invoiced"
              icon={Truck}
              tone="amber"
              title={`${stalledInvoiced.length} invoiced order${stalledInvoiced.length === 1 ? "" : "s"} awaiting pickup`}
              detail={`Invoiced ≥ 2 days ago — customer hasn’t collected yet.`}
              cta="View on invoicing board"
              onClick={() => navigate("/invoicing")}
            />
          )}
        </div>
      )}

      {/* Workflow Stages + Upcoming Dockets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="es-card overflow-hidden">
          <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-brand-600" />
              <h2 className="font-display text-base font-semibold text-zinc-900">Workflow Stages</h2>
            </div>
            <button onClick={() => navigate("/pipeline")} className="text-xs font-semibold text-brand-700 hover:underline flex items-center gap-1">
              View pipeline <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <ul>
            {WORKFLOW_STAGES.map((s) => (
              <li key={s} className="flex items-center justify-between px-5 py-3 border-b border-zinc-50 last:border-0">
                <span className="text-sm text-zinc-700">{s}</span>
                <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full bg-brand-50 text-brand-700 text-xs font-semibold" data-testid={`workflow-${s.replace(/\s+/g, "-").toLowerCase()}`}>
                  {workflowCounts[s]}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="es-card overflow-hidden">
          <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-brand-600" />
              <h2 className="font-display text-base font-semibold text-zinc-900">Upcoming Dockets</h2>
            </div>
            <button onClick={() => navigate("/dockets")} className="text-xs font-semibold text-brand-700 hover:underline flex items-center gap-1">
              View all <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          {upcomingDockets.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-zinc-500">No upcoming pickups.</div>
          ) : (
            <ul>
              {upcomingDockets.map((d) => {
                const co = companies.find((c) => c.id === d.Company_ID);
                const pr = products.find((p) => p.id === d.Product_ID);
                return (
                  <li key={d.id} className="flex items-center justify-between px-5 py-3 border-b border-zinc-50 last:border-0 hover:bg-zinc-50 cursor-pointer" onClick={() => navigate("/dockets")}>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-zinc-900 truncate">{co?.Company_Name || "—"}</div>
                      <div className="text-xs text-zinc-500 truncate">{pr?.Product_Name || "—"} · {d.Quantity_To_Pick_Up} units</div>
                    </div>
                    <div className="text-xs text-zinc-500 font-medium ml-3 flex-shrink-0">{d.Pickup_Deadline}</div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Pipeline distribution (kept from before, restyled) */}
      <div className="es-card p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-brand-600" />
            <h2 className="font-display text-base font-semibold text-zinc-900">Pipeline Distribution</h2>
          </div>
          <span className="text-xs text-zinc-500">total · {Object.values(stats?.orders_by_status || {}).reduce((a, b) => a + b, 0)}</span>
        </div>
        <div className="space-y-2.5">
          {ORDER_STAGES.map((stage) => {
            const count = stats?.orders_by_status?.[stage] || 0;
            const total = Math.max(1, ...ORDER_STAGES.map((s) => stats?.orders_by_status?.[s] || 0));
            const pct = (count / total) * 100;
            return (
              <div key={stage} className="flex items-center gap-3 text-sm">
                <div className="w-44 text-zinc-700">{stage}</div>
                <div className="flex-1 h-6 bg-zinc-100 rounded-full relative overflow-hidden">
                  <div className="h-full bg-brand-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
                <div className="w-10 text-right text-xs font-semibold text-zinc-700">{count}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick-Add modals */}
      {quickAdd === "order" && (
        <NewOrderModal
          companies={companies}
          products={products}
          onClose={() => setQuickAdd(null)}
          onCreated={(o) => { setOrders((prev) => [o, ...prev]); setQuickAdd(null); }}
        />
      )}
      {quickAdd === "docket" && (
        <NewDocketModal
          companies={companies}
          products={products}
          onClose={() => setQuickAdd(null)}
          onCreated={(d) => { setDockets((prev) => [d, ...prev]); setQuickAdd(null); }}
        />
      )}
      {quickAdd === "company" && (
        <NewCompanyModal
          onClose={() => setQuickAdd(null)}
          onCreated={(c) => { setCompanies((prev) => [c, ...prev]); setQuickAdd(null); }}
        />
      )}
      {quickAdd === "contact" && (
        <NewContactModal
          companies={companies}
          onClose={() => setQuickAdd(null)}
          onCreated={() => { setQuickAdd(null); }}
        />
      )}
      {quickAdd === "product" && (
        <NewInventoryModal
          onClose={() => setQuickAdd(null)}
          onCreated={(p) => { setProducts((prev) => [p, ...prev]); setQuickAdd(null); }}
        />
      )}
    </div>
  );
}

function KpiTile({ label, value, icon: Icon, accent, testid }) {
  return (
    <div data-testid={testid} className="es-card p-5 flex items-start justify-between">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{label}</div>
        <div className="font-display text-3xl font-bold tracking-tight text-zinc-900 mt-2">{value}</div>
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  );
}

function QuickAction({ children, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="es-btn-outline"
    >
      <Plus className="w-4 h-4 text-zinc-500" />
      {children}
    </button>
  );
}

function AlertCard({ icon: Icon, tone, title, detail, cta, onClick, testid }) {
  const tones = {
    red:   "border-l-red-500 bg-red-50/40 text-red-900",
    amber: "border-l-amber-500 bg-amber-50/40 text-amber-900",
  };
  const iconTones = { red: "text-red-600", amber: "text-amber-600" };
  return (
    <div data-testid={testid} className={`es-card border-l-4 ${tones[tone]} p-5 flex items-start gap-4`}>
      <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${iconTones[tone]}`} />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-zinc-900">{title}</div>
        <div className="text-sm text-zinc-600 mt-0.5 truncate">{detail}</div>
      </div>
      <button onClick={onClick} className="es-btn-secondary flex-shrink-0">
        {cta} <ChevronRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
